import React, { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { api, fetchCentersFromDB, isAdminUser, getCurrentMonth } from "@/lib/api";
import { toast } from "sonner";
import { PayrollReconciliationPanel } from "@/components/PayrollReconciliationPanel";
import { EmployeeEligibilityPanel } from "@/components/EmployeeEligibilityPanel";
import { RemoveFromPayrollDialog } from "@/components/RemoveFromPayrollDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import FranchiseSalaryVisibilityPanel from "./FranchiseSalaryVisibilityPanel";
import HistoricalPayrollRecovery from "./HistoricalPayrollRecovery";
import { MgtAllocationSection } from "./MgtAllocationSection";
import { SalaryReconciliationStrip } from "./SalaryReconciliationStrip";
import { WhyDaysPopover } from "./WhyDaysPopover";
import { UnlockPayrollDialog, LockHistoryDialog } from "./PayrollLockDialogs";
import SalaryRateHistoryPanel from "./SalaryRateHistoryPanel";
import { 
  Loader2, 
  Download, 
  FileSpreadsheet,
  FileText,
  Building2,
  Lock,
  Eye,
  Users,
  IndianRupee,
  Calculator,
  Globe
} from "lucide-react";

export default function Salary({ isPayslips = false }) {
  const [removeTarget, setRemoveTarget] = useState(null);
  const { session } = useAuth();
  const [month, setMonth] = useState(getCurrentMonth());
  const [targetCenter, setTargetCenter] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [salaryData, setSalaryData] = useState(null);
  
  // Payslips specific
  const [period, setPeriod] = useState("1");
  const [fmt, setFmt] = useState("pdf");
  const [employeeName, setEmployeeName] = useState("");
  const [payslipMode, setPayslipMode] = useState("bulk");
  const [signatory, setSignatory] = useState("sandeep");
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeListLoading, setEmployeeListLoading] = useState(false);
  const [centerCountry, setCenterCountry] = useState("India");

  const [centersList, setCentersList] = useState([]);

  // Fetch centers from DB on mount
  useEffect(() => {
    if (session?.token) {
      // iter269 — Salary/Payroll must include PB-MGT (HQ has employees).
      fetchCentersFromDB(session.token, "attendance").then(setCentersList);
    }
  }, [session?.token]);

  // Fetch employee list when center changes (for payslip employee dropdown)
  useEffect(() => {
    if (isPayslips && targetCenter && targetCenter !== "ALL" && session?.token) {
      setEmployeeListLoading(true);
      setEmployeeName("");
      api.post("/payslip_employees", { token: session.token, center: targetCenter })
        .then(res => {
          setEmployeeList(res.data.employees || []);
          setCenterCountry(res.data.country || "India");
        })
        .catch(() => {
          setEmployeeList([]);
          setCenterCountry("India");
        })
        .finally(() => setEmployeeListLoading(false));
    } else {
      setEmployeeList([]);
      if (!targetCenter) setCenterCountry("India");
    }
  }, [isPayslips, targetCenter, session?.token]);

  // All centers for selection + "ALL" option
  const centerOptions = [
    { code: "ALL", name: "All Centers (Combined)" },
    ...centersList
  ];

  // Preview salary on screen
  const previewSalary = async () => {
    if (!targetCenter) {
      toast.error("Please select a center first");
      return;
    }
    
    setPreviewLoading(true);
    setSalaryData(null);
    try {
      // Single backend call handles both ALL and individual centers
      const res = await api.post("/salary_preview", {
        token: session.token,
        month: month,
        targetCenter: targetCenter
      });
      
      setSalaryData(res.data);
      toast.success(`Loaded salary data for ${res.data.employeeCount} employees`);
    } catch (e) {
      const errorMsg = await parseErrorFromBlob(e);
      toast.error(errorMsg || "Failed to load salary preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Helper to parse error from blob response
  const parseErrorFromBlob = async (error) => {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        return json.detail || "An error occurred";
      } catch {
        return "An error occurred";
      }
    }
    return error.response?.data?.detail || error.message || "An error occurred";
  };

  // Generate and download salary Excel
  const generateSalary = async () => {
    if (!targetCenter) {
      toast.error("Please select a center first");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/generate_salary", {
        token: session.token,
        center: session.center,
        month: month,
        mode: targetCenter === "ALL" ? "all" : "single",
        targetCenter: targetCenter === "ALL" ? null : targetCenter
      }, {
        responseType: 'blob'
      });
      
      // Check if response is an error (JSON) instead of file
      if (res.data.type === 'application/json') {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.error(json.detail || "Failed to generate salary");
        return;
      }
      
      // Create download link
      const blob = new Blob([res.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Salary_${targetCenter}_${month}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Salary Excel downloaded!");
    } catch (e) {
      const errorMsg = await parseErrorFromBlob(e);
      toast.error(errorMsg || "Failed to generate salary");
    } finally {
      setLoading(false);
    }
  };

  // Generate payslips
  const generatePayslips = async () => {
    if (!targetCenter || targetCenter === "ALL") {
      toast.error("Please select a specific center (not ALL)");
      return;
    }
    if (payslipMode === "single" && !employeeName.trim()) {
      toast.error("Enter employee name for single mode");
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/payslips_generate", {
        token: session.token,
        center: session.center,
        month: month,
        period: period,
        fmt: fmt,
        mode: payslipMode,
        targetCenter: targetCenter,
        employeeName: payslipMode === "single" ? employeeName.trim().toUpperCase() : null,
        signatory: signatory
      }, {
        responseType: 'blob'
      });
      
      // Check if response is an error (JSON) instead of file
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.error(json.detail || "Failed to generate payslips");
        return;
      }
      
      // Determine file type
      const isZip = contentType.includes('zip');
      const blob = new Blob([res.data], { 
        type: isZip ? 'application/zip' : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = isZip 
        ? `Payslips_${targetCenter}_${month}.zip` 
        : `Payslip_${payslipMode === "single" ? employeeName.trim().toUpperCase() : targetCenter}_${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Payslip(s) downloaded!");
    } catch (e) {
      const errorMsg = await parseErrorFromBlob(e);
      toast.error(errorMsg || "Failed to generate payslips");
    } finally {
      setLoading(false);
    }
  };

  // Format currency - location-aware
  const formatCurrency = (amount) => {
    if (centerCountry !== "India") {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const isIntlCenter = centerCountry !== "India";

  if (!isAdminUser(session)) {
    return (
      <div className="text-center py-20">
        <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
        <h2 className="text-2xl font-bold text-muted-foreground">Access Denied</h2>
        <p className="text-muted-foreground mt-2">Only Admin users can access salary & payslips</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RemoveFromPayrollDialog open={!!removeTarget} onClose={() => setRemoveTarget(null)} token={session?.token}
        month={month} target={removeTarget} onDone={() => previewSalary()} />
      <div>
        <h1 className="text-3xl font-bold text-primary" data-testid="salary-page-title">
          {isPayslips ? "Payslip Generation" : "Salary Generation"}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isPayslips 
            ? "Generate PDF payslips for employees" 
            : "View and generate salary for ICICI bank upload"}
        </p>
        {!isPayslips && isAdminUser(session) && (
          <div className="mt-4"><PayrollReconciliationPanel session={session} month={month} />
            <div className="mt-4"><EmployeeEligibilityPanel session={session} month={month} /></div></div>
        )}
        {isPayslips && isIntlCenter && (
          <Badge className="mt-2 bg-blue-100 text-blue-800 hover:bg-blue-100" data-testid="intl-payroll-badge">
            <Globe className="w-3 h-3 mr-1" /> {centerCountry} - Hourly Payroll (WA Format)
          </Badge>
        )}
      </div>

      {/* Controls Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isPayslips ? <FileText className="w-5 h-5" /> : <Calculator className="w-5 h-5" />}
            {isPayslips ? "Payslip Options" : "Salary Options"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Month */}
            <div className="space-y-2">
              <Label>Month</Label>
              <Input
                type="month"
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                  setSalaryData(null);
                }}
                data-testid="salary-month"
              />
            </div>

            {/* Center Selection */}
            <div className="space-y-2">
              <Label>Select Center</Label>
              <div className="flex items-center gap-2">
                <Select value={targetCenter} onValueChange={(v) => {
                  setTargetCenter(v);
                  setSalaryData(null);
                }}>
                  <SelectTrigger data-testid="center-select" className="flex-1">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Select center" />
                  </SelectTrigger>
                  <SelectContent>
                    {centerOptions.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code === "ALL" ? "ALL CENTERS (Combined)" : `${c.code} - ${c.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {targetCenter && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => { setTargetCenter(""); setSalaryData(null); }}
                    title="Clear selection"
                    data-testid="clear-center-btn"
                  >
                    <span className="text-lg font-bold">&times;</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Payslips specific fields */}
            {isPayslips && (
              <>
                <div className="space-y-2">
                  <Label>Period</Label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Format</Label>
                  <Select value={fmt} onValueChange={setFmt}>
                    <SelectTrigger data-testid="format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="docx">DOCX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Signatory</Label>
                  <Select value={signatory} onValueChange={setSignatory}>
                    <SelectTrigger data-testid="signatory-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandeep">Sandeep Gadhwal</SelectItem>
                      <SelectItem value="jayanti">Jayanti Kathale (with Seal)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={payslipMode} onValueChange={(v) => {
                    setPayslipMode(v);
                    setEmployeeName("");
                  }}>
                    <SelectTrigger data-testid="mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bulk">All Employees</SelectItem>
                      <SelectItem value="single">Single Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {payslipMode === "single" && (
                  <div className="space-y-2">
                    <Label>Select Employee</Label>
                    {employeeListLoading ? (
                      <div className="flex items-center gap-2 h-10 px-3 border rounded-md text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading employees...
                      </div>
                    ) : employeeList.length > 0 ? (
                      <Select value={employeeName} onValueChange={setEmployeeName}>
                        <SelectTrigger data-testid="employee-select">
                          <Users className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          {employeeList.map((emp) => (
                            <SelectItem key={emp.name} value={emp.name}>
                              {emp.name} {emp.designation ? `(${emp.designation})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="h-10 flex items-center px-3 border rounded-md text-sm text-muted-foreground">
                        {targetCenter ? "No employees found for this center" : "Select a center first"}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {!isPayslips && (
              <Button
                onClick={previewSalary}
                disabled={previewLoading || !targetCenter}
                variant="secondary"
                data-testid="preview-btn"
              >
                {previewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview Salary
              </Button>
            )}
            
            <Button
              onClick={isPayslips ? generatePayslips : generateSalary}
              disabled={loading || !targetCenter || (isPayslips && targetCenter === "ALL")}
              data-testid="generate-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isPayslips ? "Generate Payslips" : "Download Excel"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Salary Preview Table (only for salary page) */}
      {!isPayslips && salaryData && (
        <Card data-testid="salary-preview-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Salary for {salaryData.center} - {salaryData.month}
              </span>
              <div className="flex items-center gap-2">
                {(salaryData.historical || (salaryData.historical_overridden_centers && salaryData.historical_overridden_centers.length > 0)) && (
                  <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300" data-testid="historical-badge">
                    HISTORICAL PAYROLL SNAPSHOT
                    {salaryData.historical_locked ? " • LOCKED" : ""}
                  </Badge>
                )}
                <Badge variant="secondary">
                  {salaryData.employeeCount} Employees • {salaryData.daysInMonth} Days
                </Badge>
              </div>
            </CardTitle>
            <SalaryReconciliationStrip
              token={session.token}
              month={month}
              targetCenter={targetCenter}
              canResolve={isAdminUser(session)}
            />
            {salaryData.historical && (
              <div className="text-xs text-muted-foreground mt-1" data-testid="historical-meta">
                Source: <strong>Recovered Salary PDF</strong>
                {salaryData.historical_invoice_number ? <> • Invoice <strong>{salaryData.historical_invoice_number}</strong></> : null}
                {salaryData.historical_invoice_date ? <> • Dated <strong>{salaryData.historical_invoice_date}</strong></> : null}
                {salaryData.historical_payment_month ? <> • Paid in <strong>{salaryData.historical_payment_month}</strong></> : null}
              </div>
            )}
            {salaryData.historical_overridden_centers && salaryData.historical_overridden_centers.length > 0 && !salaryData.historical && (
              <div className="text-xs text-muted-foreground mt-1" data-testid="historical-overrides-note">
                Historical override active for: {salaryData.historical_overridden_centers.map((c) => (
                  <Badge key={c} variant="outline" className="ml-1 text-[10px]">{c}</Badge>
                ))}
              </div>
            )}
            {/* iter175 — snapshot state + stale banner + SA actions */}
            <SnapshotStateBanner
              token={session.token}
              month={month}
              center={targetCenter}
              snapshotState={salaryData.snapshot_state}
              snapshotStale={salaryData.snapshot_stale}
              snapshotStaleReason={salaryData.snapshot_stale_reason}
              onRefresh={previewSalary}
              canEdit={!!(session.is_super_admin || session.is_admin)}
              isSuperAdmin={!!session.is_super_admin}
            />
          </CardHeader>
          <CardContent>
            {/* Totals Summary */}
            <div className={`grid gap-4 mb-6 ${
              salaryData.mgt_allocation?.eligible && salaryData.mgt_allocation?.selected_center_included
                ? "grid-cols-2 md:grid-cols-5" : "grid-cols-3"}`}>
              <Card className="bg-green-50 dark:bg-green-950">
                <CardContent className="pt-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Gross</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(salaryData.totals.gross)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 dark:bg-orange-950">
                <CardContent className="pt-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Advance</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(salaryData.totals.advance)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-primary/10">
                <CardContent className="pt-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Net</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(salaryData.totals.net)}
                  </p>
                </CardContent>
              </Card>
              {salaryData.mgt_allocation?.eligible && salaryData.mgt_allocation?.selected_center_included && (
                <>
                  <Card className="bg-purple-50 border-purple-200" data-testid="tile-mgt-allocation">
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">MGT Salary Allocation</p>
                      <p className="text-2xl font-bold text-purple-700">
                        {formatCurrency(salaryData.mgt_allocation.selected_center_amount)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-indigo-50 border-indigo-200" data-testid="tile-total-manpower-cost">
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">Total Manpower Cost</p>
                      <p className="text-2xl font-bold text-indigo-700">
                        {formatCurrency(
                          (Number(salaryData.totals.gross || 0)
                            + Number(salaryData.mgt_allocation.selected_center_amount || 0))
                        )}
                      </p>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Employee Table */}
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Employee</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Center{targetCenter === 'ALL' ? 's Worked' : ''}</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Advance</TableHead>
                    <TableHead className="text-right">Net Salary</TableHead>
                    {session?.is_super_admin && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salaryData.salaryData.map((emp, idx) => (
                    <TableRow key={idx} data-testid={`salary-row-${idx}`}
                      className={emp.salary_rate_missing ? "bg-amber-50" : ""}>
                      <TableCell className="font-medium">
                        {emp.employeeName}
                        {emp.historical && !emp.salary_rate_missing && (
                          <Badge className="ml-2 bg-slate-200 text-slate-800 hover:bg-slate-200 text-[10px]" title="Historical employee — created via bulk attendance import">HIST</Badge>
                        )}
                        {emp.salary_rate_missing && (
                          <Badge className="ml-2 bg-amber-200 text-amber-900 hover:bg-amber-200 text-[10px]" title="Historical employee has no monthly salary rate — set one to compute her salary">SALARY RATE MISSING</Badge>
                        )}
                      </TableCell>
                      <TableCell>{emp.designation}</TableCell>
                      <TableCell>
                        {targetCenter === 'ALL' && emp.centersWorked && emp.centersWorked.includes(' + ') ? (
                          <span className="inline-flex flex-wrap gap-1" data-testid={`centers-worked-${emp.employeeName}`}>
                            {emp.centersWorked.split(' + ').map((c) => (
                              <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>
                            ))}
                          </span>
                        ) : (
                          <Badge variant="outline">{emp.centersWorked || emp.center}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(emp.monthlySalary)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">{emp.presentDays}/{emp.daysInMonth}</Badge>
                        <WhyDaysPopover emp={emp} />
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(emp.grossSalary)}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        {emp.advance > 0 ? `-${formatCurrency(emp.advance)}` : "-"}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {emp.salary_rate_missing ? "—" : formatCurrency(emp.netSalary)}
                      </TableCell>
                      {session?.is_super_admin && (
                        <TableCell>
                          {emp.employeeId && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`salary-remove-${idx}`}
                              onClick={() => setRemoveTarget({ employeeId: emp.employeeId, employeeName: emp.employeeName,
                                center: targetCenter === "ALL" ? "ALL" : targetCenter, gross: emp.grossSalary,
                                net: emp.netSalary, payableDays: emp.presentDays })}>Remove</Button>)}
                        </TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* iter176 — MGT Salary Cost Allocation (July 2026+) */}
            <MgtAllocationSection
              token={session.token}
              month={month}
              center={targetCenter}
              mgt={salaryData.mgt_allocation}
              isSuperAdmin={!!session.is_super_admin}
              canEdit={!!(session.is_super_admin || session.is_admin)}
              onRefresh={previewSalary}
            />
          </CardContent>
        </Card>
      )}

      {/* Help Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <h4 className="font-bold text-sm mb-2">
            {isPayslips ? "Payslip Information" : "Salary Calculation Rules"}
          </h4>
          {isPayslips ? (
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Select a specific center to generate payslips</li>
              {isIntlCenter ? (
                <>
                  <li>Payslips use <strong>Australian WA payroll format</strong> (hourly rate, PAYG, Medicare, Super)</li>
                  <li>Hours are pulled from International Attendance records</li>
                  <li>Take-home rate is reverse-calculated to Gross using PAYG tax brackets</li>
                  <li>Super (12%) is on top of Gross, not deducted from Net</li>
                </>
              ) : (
                <>
                  <li>Use &quot;Single Employee&quot; mode for individual payslip</li>
                  <li>Enter FULL employee name in CAPS (e.g., EKTA SURESHKUMAR RAVAL)</li>
                  <li>PDF format recommended for printing</li>
                </>
              )}
            </ul>
          ) : (
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Select &quot;ALL CENTERS&quot; to view combined salary for all locations</li>
              <li>• <Badge variant="outline" className="status-P">P</Badge> Present = 1 day</li>
              <li>• <Badge variant="outline" className="status-HD">HD</Badge> Half Day = 0.5 day</li>
              <li>• <Badge variant="outline" className="status-WO">WO</Badge> Weekly Off = 1 day (paid)</li>
              <li>• <Badge variant="outline" className="status-L">L</Badge> Leave = 0 days (unpaid)</li>
              <li>• <Badge variant="outline" className="status-A">A</Badge> Absent = 0 days</li>
              <li>• <strong>Net = (Monthly Salary ÷ Days in Month × Present Days) - Advances</strong></li>
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Franchise Salary Visibility — Super Admin / Accounts only (iter166) */}
      {!isPayslips && (session?.is_super_admin || session?.is_admin || session?.role_key === 'accountant' || session?.roles?.accounts || session?.roles?.mis) && (
        <FranchiseSalaryVisibilityPanel session={session} centers={centersList} />
      )}

      {/* Historical Payroll Recovery — Super Admin only (iter170) */}
      {!isPayslips && session?.is_super_admin && (
        <HistoricalPayrollRecovery session={session} />
      )}

      {/* iter266 — Central Salary Rate History view (Admin only, not on Payslips page) */}
      {!isPayslips && (session?.is_super_admin || session?.is_admin) && (
        <SalaryRateHistoryPanel session={session} />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// iter175 — Snapshot state banner + SA lifecycle actions
// ─────────────────────────────────────────────────────────────────────
function SnapshotStateBanner({ token, month, center, snapshotState,
                                    snapshotStale, snapshotStaleReason,
                                    onRefresh, canEdit, isSuperAdmin }) {
  const [busy, setBusy] = useState(false);
  const [diff, setDiff] = useState(null);
  const [showSync, setShowSync] = useState(false);
  const state = (snapshotState || "LIVE").toUpperCase();

  const tone = state === "FINAL" ? "bg-emerald-50 border-emerald-300 text-emerald-900"
    : state === "HISTORICAL_PDF" ? "bg-amber-50 border-amber-300 text-amber-900"
    : snapshotStale ? "bg-red-50 border-red-300 text-red-900"
    : "bg-blue-50 border-blue-300 text-blue-900";

  const label = state === "FINAL" ? "FINAL — Locked Payroll"
    : state === "HISTORICAL_PDF" ? "HISTORICAL PDF — Locked Recovery"
    : snapshotStale ? "STALE — Source data changed"
    : "LIVE — Preview from current sources";

  const loadDiff = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payroll_snapshot_diff", { token, month });
      setDiff(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load diff");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!window.confirm(
      `Regenerate the payroll snapshot for ${month}? This will replace the stored snapshot (previous totals preserved in history).`,
    )) return;
    setBusy(true);
    try {
      await api.post("/payroll_snapshot_regenerate", { token, month });
      toast.success("Snapshot regenerated");
      setDiff(null);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally { setBusy(false); }
  };

  const finalise = async () => {
    if (!window.confirm(
      `Finalise (lock) payroll for ${month}? Once FINAL, future reads return the frozen rows. Only Super Admin can unlock.`,
    )) return;
    setBusy(true);
    try {
      try {
        await api.post("/payroll_snapshot_finalise", { token, month });
      } catch (e) {
        if (e.response?.status !== 409 || !window.confirm(
          `${e.response?.data?.detail}\n\nApprove the changes and finalise anyway?`)) throw e;
        await api.post("/payroll_snapshot_finalise", { token, month, approve_changes: true });
      }
      toast.success(`Payroll ${month} — FINALISED`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finalise failed");
    } finally { setBusy(false); }
  };

  const unlock = async () => {
    const reason = window.prompt(
      `Unlock the FINAL payroll for ${month}? Enter a reason (min 3 chars, preserved in audit).`,
    );
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post("/payroll_snapshot_unlock", { token, month, reason: reason.trim() });
      toast.success(`Payroll ${month} — UNLOCKED`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  };

  return (
    <div className={`mt-3 p-3 rounded border text-xs ${tone}`}
          data-testid="snapshot-state-banner">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold">
          {label}
          {snapshotStale && snapshotStaleReason && (
            <span className="font-normal ml-2 italic">— {snapshotStaleReason}</span>
          )}
        </div>
        {canEdit && state !== "HISTORICAL_PDF" && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => setShowSync(true)}
              className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
              data-testid="btn-sync-from-attendance">Sync from Attendance</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={loadDiff}
              data-testid="btn-preview-diff">Preview Differences</Button>
            {state === "FINAL" ? (
              isSuperAdmin ? (
                <Button size="sm" variant="destructive" disabled={busy} onClick={unlock}
                  data-testid="btn-unlock-payroll">Unlock</Button>
              ) : null
            ) : (
              <>
                <Button size="sm" variant="outline" disabled={busy} onClick={regenerate}
                  data-testid="btn-regenerate-snapshot">Regenerate</Button>
                <Button size="sm" variant="default" disabled={busy} onClick={finalise}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="btn-finalise-payroll">Finalise Payroll</Button>
              </>
            )}
          </div>
        )}
      </div>
      {diff && (
        <SnapshotDiffTable diff={diff} onClose={() => setDiff(null)} />
      )}
      {showSync && (
        <SyncFromAttendanceModal
          token={token}
          month={month}
          center={center}
          isFinal={state === "FINAL"}
          onClose={() => setShowSync(false)}
          onApplied={() => { setShowSync(false); onRefresh && onRefresh(); }}
        />
      )}
    </div>
  );
}


function SnapshotDiffTable({ diff, onClose }) {
  const changes = diff.changes || [];
  return (
    <div className="mt-3 border-t pt-2" data-testid="snapshot-diff-table">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-[13px]">
          Differences vs Stored Snapshot
          <span className="ml-2 text-[11px] font-normal opacity-80">
            {changes.length} changed • {(diff.added || []).length} added
            • {(diff.removed || []).length} removed
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="btn-diff-close">Close</Button>
      </div>
      {diff.totals_delta && (
        <div className="text-[11px] mb-2 flex gap-4">
          <span>Gross Δ: <strong>{diff.totals_delta.gross}</strong></span>
          <span>Advance Δ: <strong>{diff.totals_delta.advance}</strong></span>
          <span>Net Δ: <strong>{diff.totals_delta.net}</strong></span>
        </div>
      )}
      <div className="max-h-64 overflow-auto border rounded bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="p-1 text-left">Employee</th>
              <th className="p-1">Change</th>
              <th className="p-1 text-right">Old Salary</th>
              <th className="p-1 text-right">New Salary</th>
              <th className="p-1 text-right">Old Days</th>
              <th className="p-1 text-right">New Days</th>
              <th className="p-1 text-right">Old Gross</th>
              <th className="p-1 text-right">New Gross</th>
              <th className="p-1">Source</th>
            </tr>
          </thead>
          <tbody>
            {changes.length === 0 && (
              <tr><td colSpan={9} className="p-2 text-center text-muted-foreground">No changes</td></tr>
            )}
            {changes.map((c, i) => (
              <tr key={i} className="border-t">
                <td className="p-1 font-mono">{c.employee}</td>
                <td className="p-1">
                  <span className={c.change === "ADDED"
                    ? "text-emerald-700 font-semibold"
                    : "text-amber-800 font-semibold"}>{c.change}</span>
                </td>
                <td className="p-1 text-right">{c.old_salary ?? "—"}</td>
                <td className="p-1 text-right">{c.new_salary}</td>
                <td className="p-1 text-right">{c.old_days ?? "—"}</td>
                <td className="p-1 text-right">{c.new_days}</td>
                <td className="p-1 text-right">{c.old_gross ?? "—"}</td>
                <td className="p-1 text-right">{c.new_gross}</td>
                <td className="p-1 text-[10px]">{c.salary_source || ""}</td>
              </tr>
            ))}
            {(diff.removed || []).map((n, i) => (
              <tr key={`r-${i}`} className="border-t bg-red-50/60">
                <td className="p-1 font-mono">{n}</td>
                <td className="p-1"><span className="text-red-700 font-semibold">REMOVED</span></td>
                <td colSpan={7} className="p-1 text-[11px] text-red-800">
                  In stored snapshot but not in live calc
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// iter175c — Sync From Attendance modal
// ─────────────────────────────────────────────────────────────────────
function SyncFromAttendanceModal({ token, month, center, isFinal,
                                       onClose, onApplied }) {
  const [mode, setMode] = useState("center"); // "center" | "employee"
  const [empName, setEmpName] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showResolver, setShowResolver] = useState(false);

  const load = async () => {
    setLoading(true); setPreview(null);
    try {
      const body = { token, month };
      if (center && center !== "all" && center !== "ALL") body.center = center;
      if (mode === "employee" && empName.trim()) body.employee_name = empName.trim();
      const r = await api.post("/payroll_sync_preview", body);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = preview?.rows.filter((r) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "changed")
      return r.status === "DIFFERENCE" || r.status === "MISSING_FROM_PAYROLL";
    return r.status === statusFilter;
  }) || [];

  const apply = async () => {
    if (isFinal) {
      toast.error("Payroll is FINAL. Unlock it first.");
      return;
    }
    if (reason.trim().length < 3) {
      toast.error("Reason (min 3 chars) is required.");
      return;
    }
    setBusy(true);
    try {
      const body = { token, month, reason: reason.trim() };
      if (center && center !== "all" && center !== "ALL") body.center = center;
      if (mode === "employee" && empName.trim())
        body.employee_names = [empName.trim()];
      const r = await api.post("/payroll_sync_apply", body);
      const d = r.data || {};
      const parts = [];
      if (d.inserted?.length) parts.push(`+${d.inserted.length} inserted`);
      if (d.updated?.length)  parts.push(`~${d.updated.length} updated`);
      if (d.removed?.length)  parts.push(`-${d.removed.length} removed`);
      const summary = parts.length ? parts.join(" · ") : "no changes";
      toast.success(`Sync applied — ${summary} (total ${d.employee_count})`);
      if (d.blocked_salary_rate_missing?.length) {
        toast.warning(`Blocked (rate missing): ${d.blocked_salary_rate_missing.join(", ")}`);
      }
      if (d.reconcile_mismatches?.length) {
        toast.warning(`Skipped ${d.reconcile_mismatches.length} row(s) — attendance/days mismatch. Review these in Preview.`);
      }
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  const totalChangedProposedGross = filtered.reduce(
    (s, r) => s + (Number(r.proposed_gross) || 0), 0);
  const totalCurrentGross = filtered.reduce(
    (s, r) => s + (Number(r.current_gross) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="sync-modal">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#8B0000] text-lg">Sync From Attendance</div>
            <div className="text-xs text-muted-foreground">
              {center || "ALL"} • {month} • Payable rule: P=1, WO=1, HD=0.5, A=0, L=0
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}
                    data-testid="sync-cancel">Close</Button>
        </div>

        {isFinal && (
          <div className="p-2 bg-red-50 border-b border-red-300 text-xs text-red-800">
            <strong>Payroll is FINAL (locked).</strong> Unlock the payroll first (with a reason)
            before applying any sync. Preview below is read-only.
          </div>
        )}

        <div className="p-3 border-b bg-slate-50/60 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Scope</Label>
            <div className="flex gap-2 mt-1">
              <Button size="sm" variant={mode === "center" ? "default" : "outline"}
                onClick={() => setMode("center")}
                data-testid="sync-mode-center">Whole {center || "All Centres"}</Button>
              <Button size="sm" variant={mode === "employee" ? "default" : "outline"}
                onClick={() => setMode("employee")}
                data-testid="sync-mode-employee">One Employee</Button>
            </div>
          </div>
          {mode === "employee" && (
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Employee name</Label>
              <Input value={empName} onChange={(e) => setEmpName(e.target.value)}
                placeholder="e.g. SAVITA JADHAV"
                data-testid="sync-emp-input" />
            </div>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            data-testid="sync-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && (
            <div className="ml-auto text-[11px] text-muted-foreground flex flex-wrap gap-3">
              <span>Total: <strong>{preview.summary.total}</strong></span>
              <span className="text-emerald-700">Matched: <strong>{preview.summary.matched}</strong></span>
              <span className="text-amber-800">Differing: <strong>{preview.summary.differing}</strong></span>
              <span className="text-blue-700">Missing from payroll: <strong>{preview.summary.missing_from_payroll}</strong></span>
              <span className="text-red-700">Missing attendance: <strong>{preview.summary.missing_from_attendance}</strong></span>
              <span className="text-fuchsia-700">
                Rate missing: <strong>{preview.summary.salary_rate_missing}</strong>
              </span>
              {preview.summary.salary_rate_missing > 0 && (
                <button
                  onClick={() => { setStatusFilter("SALARY_RATE_MISSING"); setShowResolver(true); }}
                  data-testid="open-resolve-missing-rates"
                  className="ml-1 px-2 py-0.5 text-[11px] font-semibold rounded border border-fuchsia-400 bg-fuchsia-100 text-fuchsia-900 hover:bg-fuchsia-200"
                >
                  Resolve Missing Salary Rates →
                </button>
              )}
            </div>
          )}
        </div>

        {showResolver && preview && (
          <ResolveMissingRatesDialog
            token={token} month={month} center={center}
            rows={preview.rows.filter((r) => r.status === "SALARY_RATE_MISSING")}
            onClose={() => setShowResolver(false)}
            onSaved={() => { setShowResolver(false); load(); }}
          />
        )}

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading preview…
            </div>
          )}
          {preview && (
            <>
              <div className="px-3 pt-2 flex items-center gap-2 text-xs">
                <span>Filter:</span>
                {["all", "changed", "MATCHED", "DIFFERENCE", "MISSING_FROM_PAYROLL",
                    "MISSING_FROM_ATTENDANCE", "SALARY_RATE_MISSING"].map((s) => (
                  <button key={s} onClick={() => setStatusFilter(s)}
                    className={`px-2 py-0.5 rounded border ${
                      statusFilter === s
                        ? "bg-red-100 border-red-400 text-red-800"
                        : "bg-white border-gray-200 hover:bg-gray-50"}`}
                    data-testid={`sync-filter-${s}`}>{s}</button>
                ))}
              </div>
              <table className="w-full text-[11px] mt-2">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-1 text-left">Employee</th>
                    <th className="p-1">Centre</th>
                    <th className="p-1 text-right">Monthly</th>
                    <th className="p-1 text-right">Cur Days</th>
                    <th className="p-1 text-right">P</th>
                    <th className="p-1 text-right">WO</th>
                    <th className="p-1 text-right">HD</th>
                    <th className="p-1 text-right">A</th>
                    <th className="p-1 text-right">L</th>
                    <th className="p-1 text-right text-slate-500">Unmarked</th>
                    <th className="p-1 text-right">Prop Days</th>
                    <th className="p-1 text-right">Cur Gross</th>
                    <th className="p-1 text-right">Prop Gross</th>
                    <th className="p-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={14} className="p-4 text-center text-muted-foreground">
                      No rows for this filter
                    </td></tr>
                  )}
                  {filtered.map((r, i) => {
                    const tone = r.status === "MATCHED"
                      ? "bg-emerald-50/40"
                      : r.status === "DIFFERENCE"
                      ? "bg-amber-50/60"
                      : r.status === "MISSING_FROM_PAYROLL"
                      ? "bg-blue-50/60"
                      : r.status === "MISSING_FROM_ATTENDANCE"
                      ? "bg-red-50/60"
                      : r.status === "SALARY_RATE_MISSING"
                      ? "bg-fuchsia-50/70"
                      : "";
                    const hasWarn = (r.also_worked_at?.length || 0) > 0
                      || r.reconciles === false;
                    return (
                      <tr key={i} className={`border-t ${tone}`}>
                        <td className="p-1 font-mono">
                          {r.employee}
                          {hasWarn && (
                            <span className="ml-1 text-[9px] text-amber-800"
                                     title={r.also_worked_at?.length
                                       ? `Also worked at: ${r.also_worked_at.join(", ")}`
                                       : "Attendance breakdown does not reconcile with proposed days"}>
                              ⚠
                            </span>
                          )}
                        </td>
                        <td className="p-1">{r.center}</td>
                        <td className="p-1 text-right">{r.monthly_salary?.toLocaleString?.() || "—"}</td>
                        <td className="p-1 text-right">{r.current_days ?? "—"}</td>
                        <td className="p-1 text-right">{r.attendance?.P ?? 0}</td>
                        <td className="p-1 text-right">{r.attendance?.WO ?? 0}</td>
                        <td className="p-1 text-right">{r.attendance?.HD ?? 0}</td>
                        <td className="p-1 text-right">{r.attendance?.A ?? 0}</td>
                        <td className="p-1 text-right">{r.attendance?.L ?? 0}</td>
                        <td className="p-1 text-right text-slate-500">{r.unmarked ?? 0}</td>
                        <td className="p-1 text-right font-bold">{r.proposed_days ?? "—"}</td>
                        <td className="p-1 text-right">
                          {r.current_gross != null ? Number(r.current_gross).toLocaleString() : "—"}
                        </td>
                        <td className="p-1 text-right text-emerald-800 font-semibold">
                          {r.proposed_gross != null ? Number(r.proposed_gross).toLocaleString() : "—"}
                        </td>
                        <td className="p-1"><Badge variant="outline" className="text-[10px]">{r.status}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
                {filtered.length > 0 && (
                  <tfoot>
                    <tr className="border-t bg-slate-50 font-semibold">
                      <td colSpan={11} className="p-1 text-right">Filtered totals →</td>
                      <td className="p-1 text-right">{Math.round(totalCurrentGross).toLocaleString()}</td>
                      <td className="p-1 text-right text-emerald-800">{Math.round(totalChangedProposedGross).toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
              {preview.apply_blocked && (
                <div className="p-3 m-2 border-2 border-red-400 bg-red-50 text-xs text-red-900 rounded"
                        data-testid="sync-apply-blocked">
                  <strong>Apply is blocked.</strong> {preview.apply_blocked_reason}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-3 border-t bg-white flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <Label className="text-xs">Reason (audit trail) *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="e.g. Attendance corrected for Savita, re-syncing PB-SN July"
                     data-testid="sync-reason" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}
                      data-testid="sync-cancel-bottom">Cancel</Button>
            <Button variant="default" onClick={apply}
                      disabled={busy || isFinal || !preview
                                     || preview.apply_blocked
                                     || reason.trim().length < 3}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="sync-apply">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Apply &amp; Recalculate
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// iter207 — SA/HR quick-fix: Resolve SALARY_RATE_MISSING employees
// ─────────────────────────────────────────────────────────────────────
function ResolveMissingRatesDialog({ token, month, center, rows, onClose, onSaved }) {
  const [edits, setEdits] = React.useState(() =>
    (rows || []).reduce((acc, r) => {
      acc[r.employee] = { monthly_salary: 0, reason: "", update_master: false };
      return acc;
    }, {}));
  const [busy, setBusy] = React.useState(false);

  const setEdit = (name, patch) =>
    setEdits((p) => ({ ...p, [name]: { ...(p[name] || {}), ...patch } }));

  const toResolve = Object.entries(edits).filter(
    ([, v]) => Number(v.monthly_salary || 0) > 0 && (v.reason || "").trim().length >= 3
  );

  const applyAll = async () => {
    if (toResolve.length === 0) {
      toast.error("Enter salary and reason (min 3 chars) for at least one row.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        token, month,
        center: center && center !== "all" && center !== "ALL" ? center : null,
        overrides: toResolve.map(([name, v]) => ({
          employee_name: name,
          monthly_salary: Number(v.monthly_salary),
          reason: v.reason.trim(),
          update_employee_master: !!v.update_master,
        })),
      };
      const r = await api.post("/resolve_missing_rate", body);
      toast.success(`Resolved ${r.data.resolved} rate(s). Now click "Apply & Recalculate".`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save overrides");
    } finally { setBusy(false); }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="resolve-missing-rates-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold text-fuchsia-900">Resolve Missing Salary Rates</div>
            <div className="text-xs text-slate-600 mt-0.5">
              {rows.length} employee(s) with <b>SALARY_RATE_MISSING</b> for <b>{center || "ALL"} · {month}</b>.
              Enter a monthly salary + reason for each employee below. Optional: also update the Employee Master rate.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-xl leading-none"
                  data-testid="resolve-missing-close">×</button>
        </div>

        <div className="flex-1 overflow-auto p-3">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="text-left p-2">Employee</th>
                <th className="text-left p-2">Centre</th>
                <th className="text-right p-2">Days</th>
                <th className="text-right p-2">Master ₹</th>
                <th className="text-left p-2">Source</th>
                <th className="text-right p-2 w-32">Monthly Salary ₹ *</th>
                <th className="text-left p-2 w-64">Reason *</th>
                <th className="text-center p-2">Also update EM?</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const e = edits[r.employee] || {};
                return (
                  <tr key={r.employee} className="border-b border-slate-100">
                    <td className="p-2 font-medium">{r.employee}</td>
                    <td className="p-2">{r.center}</td>
                    <td className="p-2 text-right">{r.proposed_days ?? "—"}</td>
                    <td className="p-2 text-right">{r.monthly_salary || 0}</td>
                    <td className="p-2 text-slate-500">{r.salary_source || "MISSING"}</td>
                    <td className="p-2">
                      <input type="number" min={0}
                             className="w-full border border-slate-300 rounded px-2 py-1 text-right"
                             value={e.monthly_salary || ""}
                             onChange={(ev) => setEdit(r.employee, { monthly_salary: ev.target.value })}
                             data-testid={`resolve-rate-${r.employee}`}
                             placeholder="0" />
                    </td>
                    <td className="p-2">
                      <select className="w-full border border-slate-300 rounded px-2 py-1 mb-1"
                              value={e.reason && [
                                "Employee master not updated",
                                "Salary revision pending",
                                "Previous payroll rate confirmed",
                                "Employee transfer mapping issue",
                                "Other",
                              ].includes(e.reason) ? e.reason : "Other"}
                              onChange={(ev) => setEdit(r.employee, { reason: ev.target.value })}>
                        <option>Employee master not updated</option>
                        <option>Salary revision pending</option>
                        <option>Previous payroll rate confirmed</option>
                        <option>Employee transfer mapping issue</option>
                        <option>Other</option>
                      </select>
                      <input type="text"
                             className="w-full border border-slate-300 rounded px-2 py-1"
                             value={e.reason || ""}
                             onChange={(ev) => setEdit(r.employee, { reason: ev.target.value })}
                             data-testid={`resolve-reason-${r.employee}`}
                             placeholder="Type or override reason (min 3 chars)" />
                    </td>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={!!e.update_master}
                             onChange={(ev) => setEdit(r.employee, { update_master: ev.target.checked })}
                             data-testid={`resolve-update-em-${r.employee}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="text-xs text-slate-600">
            <b>{toResolve.length}</b> of <b>{rows.length}</b> rows ready to save. Empty rate rows are ignored.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={applyAll} disabled={busy || toResolve.length === 0}
                    className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                    data-testid="resolve-missing-save">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Save Overrides &amp; Refresh Preview
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
